import React from "react";
import { whatWeDo } from "../../data/whatwedo";
import Reveal from "../Reveal";
import { ArrowDown } from "lucide-react";

/**
 * WhatWeDo — the "What We Do" section for the Nyle Apparel homepage.
 * Editorial, calm, spacious. Ivory background, purple/green accents.
 * The supplied text-free ribbon artwork is the hero visual and is revealed
 * left-to-right on scroll. All headings and stage copy remain live HTML.
 */
const WhatWeDo = () => {
  // Ribbon reveal state
  const ribbonRef = React.useRef(null);
  const [ribbonIn, setRibbonIn] = React.useState(false);
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const m = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReduced(m.matches);
      const on = (e) => setReduced(e.matches);
      m.addEventListener?.("change", on);
      return () => m.removeEventListener?.("change", on);
    }
  }, []);

  React.useEffect(() => {
    const el = ribbonRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRibbonIn(true);
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const revealed = reduced || ribbonIn;
  const accentClass = (a) =>
    a === "green" ? "text-[#6FA84A]" : "text-[#7B3FA7]";

  return (
    <section
      id="what-we-do"
      className="relative scroll-mt-24 md:scroll-mt-32 bg-[#FBF6EC] text-neutral-900 overflow-hidden"
      aria-label="What We Do"
    >
      <div className="pt-12 md:pt-16 pb-24 md:pb-28">
        {/* Intro */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p
                className="font-['Montserrat'] font-medium uppercase text-[#7B3FA7] text-xs md:text-sm"
                style={{ letterSpacing: "0.18em" }}
              >
                {whatWeDo.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h2
                className="mt-5 md:mt-6 font-['Cormorant_Garamond'] text-neutral-900 leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
                style={{ fontWeight: 500, letterSpacing: "-0.005em" }}
              >
                {whatWeDo.heading}
              </h2>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-5 md:mt-6 max-w-xl font-['Montserrat'] font-normal text-neutral-500 text-base md:text-lg leading-relaxed">
                {whatWeDo.support}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Ribbon artwork — full-bleed, large hero visual */}
        <div
          ref={ribbonRef}
          className="relative mt-6 md:mt-4 w-screen left-1/2 -translate-x-1/2"
          aria-hidden={false}
        >
          <div className="relative w-full h-[36vh] sm:h-[40vh] md:h-[52vh] lg:h-[58vh]">
            <img
              src={whatWeDo.ribbonImage}
              alt="A flowing textile ribbon transitioning from sketch threads to woven purple fabric, into intertwined purple and green strands, and ending in a subtle illuminated global network — representing the Nyle Apparel journey from idea to global scale."
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover select-none"
              style={{
                clipPath: `inset(0 ${revealed ? "0%" : "100%"} 0 0)`,
                WebkitClipPath: `inset(0 ${revealed ? "0%" : "100%"} 0 0)`,
                transition: reduced
                  ? undefined
                  : "clip-path 2400ms cubic-bezier(0.22, 1, 0.36, 1) 100ms, -webkit-clip-path 2400ms cubic-bezier(0.22, 1, 0.36, 1) 100ms",
              }}
            />
          </div>
        </div>

        {/* Scroll indicator — directly below the ribbon */}
        <div className="flex justify-center mt-8 md:mt-10">
          <a
            href="#what-we-do-stages"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#what-we-do-stages");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="flex flex-col items-center gap-3 group"
            aria-label="Scroll to stages"
          >
            <span
              className="font-['Montserrat'] font-medium text-[#7B3FA7] text-[10px] md:text-[11px] uppercase"
              style={{ letterSpacing: "0.12em" }}
            >
              Scroll to explore
            </span>
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#7B3FA7]/50 bg-white/70 backdrop-blur-sm text-[#7B3FA7] group-hover:border-[#7B3FA7] group-hover:bg-white transition-colors">
              <ArrowDown
                size={18}
                className="animate-[floatY_2.4s_ease-in-out_infinite]"
              />
            </span>
          </a>
        </div>

        {/* Stages */}
        <div id="what-we-do-stages" className="max-w-[1400px] mx-auto px-6 md:px-12 scroll-mt-24 md:scroll-mt-32">
          <ol
            className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            role="list"
          >
            {whatWeDo.stages.map((s, i) => (
              <li
                key={s.num}
                className={`list-none px-4 md:px-8 py-8 md:py-10 relative ${
                  i > 0
                    ? "lg:before:content-[''] lg:before:absolute lg:before:left-0 lg:before:top-6 lg:before:bottom-6 lg:before:w-px lg:before:bg-neutral-900/12"
                    : ""
                } ${
                  i % 2 === 1
                    ? "sm:before:content-[''] sm:before:absolute sm:before:left-0 sm:before:top-6 sm:before:bottom-6 sm:before:w-px sm:before:bg-neutral-900/12 lg:before:bg-neutral-900/12"
                    : ""
                }`}
              >
                <Reveal delay={120 + i * 140}>
                  <div>
                    <p
                      className={`font-['Cormorant_Garamond'] leading-none text-4xl md:text-5xl ${accentClass(s.accent)}`}
                      style={{ fontWeight: 500 }}
                    >
                      {s.num}
                    </p>
                    <p
                      className="mt-3 md:mt-4 font-['Montserrat'] font-semibold uppercase text-neutral-900 text-sm md:text-base"
                      style={{ letterSpacing: "0.14em" }}
                    >
                      {s.label}
                    </p>
                    <p className="mt-3 font-['Montserrat'] font-normal text-neutral-600 text-sm md:text-base leading-relaxed">
                      {s.title}
                    </p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>

          {/* Closing */}
          <div className="mt-14 md:mt-20 text-center">
            <Reveal>
              <p
                className="mx-auto font-['Cormorant_Garamond'] text-neutral-800 text-2xl md:text-3xl lg:text-4xl leading-snug whitespace-nowrap"
                style={{ fontWeight: 500 }}
              >
                {whatWeDo.closing}
              </p>
            </Reveal>
            <Reveal delay={160}>
              <div
                className="mx-auto mt-6 md:mt-8 h-[2px] w-24 md:w-28 rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, #7B3FA7 0%, #7B3FA7 45%, #6FA84A 100%)",
                }}
              />
            </Reveal>
            <Reveal delay={260}>
              <p
                className="mt-5 md:mt-6 font-['Montserrat'] font-medium text-neutral-800 text-sm md:text-base"
                style={{ letterSpacing: "0.08em" }}
              >
                Build.{" "}
                <span className="text-[#7B3FA7]">Brand.</span>{" "}
                Scale with <span className="text-[#6FA84A]">NYLE</span>.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
